import { useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { antdTheme } from './theme';
import { queryClient } from './lib/queryClient';
import { applySiteIcon } from './lib/siteIcon';
import { useAuthStore } from './stores/authStore';
import { AdminLayout } from './layouts/AdminLayout';
import LoginPage from './pages/login';
import DashboardPage from './pages/dashboard';
import ApprovalsPage from './pages/approvals';
import UsersPage from './pages/users';
import CustomersPage from './pages/customers';
import OrderNotesPage from './pages/order-notes';
import OrderHistoryPage from './pages/order-history';
import MembershipPage from './pages/membership';
import PaymentPage from './pages/payment';
import PaymentTransfersPage from './pages/payment-transfers';
import DiscountCouponsPage from './pages/discount-coupons';
import PhoneReleasesPage from './pages/phone-releases';
import IdentityMergesPage from './pages/identity-merges';
import IdentityVerificationsPage from './pages/identity-verifications';
import AnnualArchivePage from './pages/annual-archive';
import FileCleanupPage from './pages/file-cleanup';
import SettingsPage from './pages/settings';
import RegisterFlowPage from './pages/settings/register-flow';
import CredentialsPage from './pages/settings/credentials';

dayjs.locale('zh-cn');

/** 管理员守卫：未登录跳登录页；登录中显示加载；非 admin 由 _hydrate 已拦截 */
function RequireAdmin() {
  const { user, loading } = useAuthStore();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spin size="large" tip="正在验证登录态..." />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'order-notes', element: <OrderNotesPage /> },
          { path: 'order-history', element: <OrderHistoryPage /> },
          { path: 'membership', element: <MembershipPage /> },
          { path: 'payment', element: <PaymentPage /> },
          { path: 'payment-transfers', element: <PaymentTransfersPage /> },
          // 推广链接与邀请记录已并入会员管理（同一业务域不再两处入口）；旧地址保留重定向，
          // 收藏与外部链接照样落在对应那一栏。
          { path: 'referrals', element: <Navigate to="/membership?tab=growth&view=referral" replace /> },
          { path: 'discount-coupons', element: <DiscountCouponsPage /> },
          { path: 'promotion-links', element: <Navigate to="/membership?tab=growth&view=promotion" replace /> },
          { path: 'phone-releases', element: <PhoneReleasesPage /> },
          { path: 'identity-merges', element: <IdentityMergesPage /> },
          { path: 'identity-verifications', element: <IdentityVerificationsPage /> },
          { path: 'annual-archive', element: <AnnualArchivePage /> },
          { path: 'file-cleanup', element: <FileCleanupPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'settings/register-flow', element: <RegisterFlowPage /> },
          { path: 'settings/credentials', element: <CredentialsPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

function App() {
  const _hydrate = useAuthStore((s) => s._hydrate);

  useEffect(() => {
    void _hydrate();
    applySiteIcon();
  }, [_hydrate]);

  return (
    <ConfigProvider locale={zhCN} theme={antdTheme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;